import React, { useState } from 'react'
import { Box } from '@mui/material'
import NextImage from 'next/image'
import { useTranslation, Trans } from 'next-i18next'
import { SignUpCallRequestAnimalSelection, useSignUpCallRequestStore } from '@/store/signUpCallRequestStore'
import { EmtTypography, EmtSeparator } from 'emt-components-v2'
import VariantImages from '@/assets/VariantImages'
import { ReservationDates } from '@/components/PetSitter/ReservationCard/ReservationDates'
import EditPetsModal from './EditPetsModal'
import EditAdditionalInformationsModal from './EditAdditionalInformationsModal'
import GenericBox from '@/components/PetSitter/GenericBox'
import NextLink from 'next/link'
import { Routes } from '@/assets/configs'
import EditLocationModal from './EditLocationModal'
import EditServiceModal from './EditServiceModal'
import EditAnimalSelectionModal from './EditAnimalSelectionModal'
import { transformToCartProducts } from '@/modules/PetSitter/tendersServiceHelper'
import { useIsResponsive } from '@/hooks'
import {
  buildRecurrentDateList,
  services,
} from '@/components/pages/pet-sitter/reservations/NewRequest/requestConfig'

interface Props {
  step: string
  animalSelection?: SignUpCallRequestAnimalSelection
  onAnimalSelectionChange?: (selection: SignUpCallRequestAnimalSelection) => void
  createMission?: boolean
}

// Types for expanded instances
type PromenadeInstance = {
  date: string
  timePeriods: { timeStart: string; serviceDuration: string }[]
}
type VisiteInstance = {
  date: string
  timePeriods: { timeStart: string; serviceDuration: string }[]
}
type GardeInstance = any // You can refine this type if needed

// Expand all slots to get correct date intervals for all services
export function expandInstancesAll(
  slots: any[],
  selectedService: string,
): (PromenadeInstance | VisiteInstance | GardeInstance)[] {
  if (!Array.isArray(slots) || !selectedService) return []
  // Try to use expandInstances if available on slot ref, else fallback to slot.data
  // This assumes ServiceContentSection/DateTimeFormRecurrent exposes expandInstances
  // If not, fallback to slot.data
  // Here, we mimic the logic of expandInstances for all services
  return slots.flatMap((slot) => {
    // If slot.ref and expandInstances exists, use it
    // But in ReservationInfos, we only have slot.data, so we expand manually
    const data = slot?.data
    if (!data) return []
    // For recurrent services (promenade, visite), expand by frequency and timePeriods
    if (selectedService === 'promenade' || selectedService === 'visite') {
      // 1. Build date list (same logic as DateTimeFormRecurrent)
      const days = buildRecurrentDateList({ ...data, customDays: data.customDays ?? data })
      const instances: (PromenadeInstance | VisiteInstance)[] = []
      for (const date of days) {
        for (const slotPeriod of data.timePeriods || []) {
          if (!slotPeriod.timeStart) continue
          if (selectedService === 'promenade') {
            instances.push({
              date,
              timePeriods: [{ timeStart: slotPeriod.timeStart, serviceDuration: slotPeriod.serviceDuration }],
            })
          } else {
            instances.push({
              date,
              timePeriods: [{ timeStart: slotPeriod.timeStart, serviceDuration: slotPeriod.serviceDuration }],
            })
          }
        }
      }
      return instances
    }
    // For "garde" services, expand start/end to all days
    if (selectedService === 'garde-chez-vous' || selectedService === 'garde-chez-eux') {
      const { start, end } = data
      if (start && end) {
        const startDate = new Date(start.split('T')[0])
        const endDate = new Date(end.split('T')[0])
        const days: any[] = []
        for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
          let dayStart = new Date(d)
          let dayEnd = new Date(d)
          if (d.getTime() === startDate.getTime()) {
            dayStart = new Date(start)
          } else {
            dayStart.setHours(0, 0, 0, 0)
          }
          if (d.getTime() === endDate.getTime()) {
            dayEnd = new Date(end)
          } else {
            dayEnd.setHours(23, 59, 59, 999)
          }
          days.push({ ...data, start: dayStart.toISOString(), end: dayEnd.toISOString() })
        }
        return days
      }
    }
    // fallback: just return the data
    return [data]
  })
}

const ReservationInfos: React.FC<Props> = ({ step, animalSelection, onAnimalSelectionChange, createMission }) => {
  const { t } = useTranslation('sign-up-call-request')
  const { t: tp } = useTranslation('petsitter')
  const { t: tpremium } = useTranslation('premium')

  const [isMobile] = useIsResponsive()

  // Zustand store
  const address = useSignUpCallRequestStore((s) => s.address)
  const service = useSignUpCallRequestStore((s) => s.service)
  const selectedServiceInfo = services.find((item) => item.slug === service?.selectedService)
  const animals = useSignUpCallRequestStore((s) => s.animals)
  const additionalInformations = useSignUpCallRequestStore((s) => s.additionalInformations)
  const icon = useSignUpCallRequestStore((s) => s.icon)

  const [editPetsOpen, setEditPetsOpen] = useState(false)
  const [editServiceOpen, setEditServiceOpen] = useState(false)
  const [editAnimalSelectionOpen, setEditAnimalSelectionOpen] = useState(false)

  // Compute dateIntervals from formSlots for transformToCartProducts, with expansion for all services
  const dateIntervals = expandInstancesAll(service?.formSlots ?? [], service?.selectedService ?? '')
  const dates = service?.selectedService
    ? transformToCartProducts(service.selectedService, dateIntervals)?.map(({ offer, ...rest }) => ({
        productSlug: offer,
        ...rest,
      }))
    : undefined

  // Modal state
  const [editLocationOpen, setEditLocationOpen] = useState(false)
  const [editAdditionalInformationsOpen, setEditAdditionalInformationsOpen] = useState(false)

  const selectedAnimalCount = animalSelection?.reduce((total, animal) => total + animal.number, 0) ?? 0
  const selectedAnimalLabels =
    animalSelection
      ?.filter((animal) => animal.number > 0)
      .map((animal) => `${animal.number} ${animal.slug === 'dog' ? 'chien' : animal.slug === 'cat' ? 'chat' : 'autre'}`)
      .join(', ') ?? ''
  const shouldShowSelectedAnimalSummary = selectedAnimalCount > 0
  const shouldShowAnimalDetails = !shouldShowSelectedAnimalSummary && animals?.length > 0

  const caracteristiquesCount = React.useMemo(() => {
    if (!additionalInformations) return 0
    let count = 0
    if (Array.isArray(additionalInformations.walkBehaviors)) {
      count += additionalInformations.walkBehaviors.length
    }
    if (Array.isArray(additionalInformations.indoorBehaviors)) {
      count += additionalInformations.indoorBehaviors.length
    }
    if (additionalInformations.indoorCleanliness) {
      count += 1
    }
    if (additionalInformations.timeAlone) {
      count += 1
    }
    return count
  }, [additionalInformations])
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        rowGap: '20px',
        position: 'relative',
        minWidth: '0',
      }}
    >
      {/* Edit Location Modal */}
      <EditLocationModal open={editLocationOpen} setOpen={setEditLocationOpen} />
      {/* Edit Service Modal */}
      <EditServiceModal open={editServiceOpen} setOpen={setEditServiceOpen} />
      {/* Edit pets modal */}
      <EditPetsModal open={editPetsOpen} setOpen={setEditPetsOpen} />
      {onAnimalSelectionChange && (
        <EditAnimalSelectionModal
          open={editAnimalSelectionOpen}
          setOpen={setEditAnimalSelectionOpen}
          animalSelection={animalSelection}
          onSave={onAnimalSelectionChange}
        />
      )}
      {/* Edit Additional Informations Modal */}
      <EditAdditionalInformationsModal
        open={editAdditionalInformationsOpen}
        setOpen={setEditAdditionalInformationsOpen}
      />

      {(!isMobile || step === 'signup' || (step === 'informations' && createMission)) && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            rowGap: '20px',
            position: 'relative',
            backgroundColor: '#fff',
            borderRadius: '16px',
            padding: '17px',
          }}
        >
          <EmtTypography variant={'h5'} fontWeight="800">
            Votre recherche
          </EmtTypography>
          <EmtSeparator />
          {address && (
            <GenericBox
              title={address?.city}
              subtitle={address?.zipCode}
              icon="emt-pin"
              actionText={step !== 'location' ? () => setEditLocationOpen(true) : undefined}
            />
          )}
          {dates && (
            <ReservationDates
              variant="showService"
              dates={dates}
              icon={icon || 'emt-calendar'}
              actionText={step !== 'service' ? () => setEditServiceOpen(true) : undefined}
            />
          )}
          {selectedServiceInfo && (
            <GenericBox
              title={selectedServiceInfo.priceLabel}
              subtitle="Le tarif varie selon les pets sitters"
              icon={selectedServiceInfo.iconClass}
            />
          )}

          {shouldShowAnimalDetails && (
            <GenericBox
              title={`${animals.length} anima${animals.length > 1 ? 'ux' : 'l'}`}
              subtitle={animals.map((a) => a.displayName).join(', ')}
              icon="emt-dog"
              actionText={step !== 'pets' ? () => setEditPetsOpen(true) : undefined}
            />
          )}

          {shouldShowSelectedAnimalSummary && (
            <GenericBox
              reverseText
              title={selectedAnimalLabels}
              subtitle="Pour quels animaux"
              icon="emt-dog"
              actionText={onAnimalSelectionChange ? () => setEditAnimalSelectionOpen(true) : undefined}
            />
          )}

          {additionalInformations && caracteristiquesCount > 0 && (
            <GenericBox
              title="Caractéristiques"
              subtitle={`${caracteristiquesCount} détail${caracteristiquesCount > 1 ? 's' : ''} ajouté${
                caracteristiquesCount > 1 ? 's' : ''
              }`}
              icon="emt-text-document-alt"
              actionText={() => setEditAdditionalInformationsOpen(true)}
            />
          )}

          {(step === 'service' || step === 'pets') && (
            <Box
              sx={{
                background: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.25) 112.85%)',
                height: '200px',
                borderRadius: '16px',
                padding: '18px 10px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                alignItems: 'center',
                position: 'relative',
                overflow: 'hidden',
                zIndex: 0,
              }}
            >
              <Box
                sx={{
                  backgroundColor: '#fff',
                  padding: '16px',
                  borderRadius: '10px',
                  zIndex: 2,
                  width: '100%',
                }}
              >
                <EmtTypography
                  variant="body1"
                  fontWeight={700}
                  sx={{
                    backgroundColor: '#fff',
                  }}
                >
                  <Trans
                    t={t}
                    i18nKey={`moreThanPetsitter.title`}
                    components={{
                      Purple: <span style={{ color: '#AE59C6' }} />,
                    }}
                  />
                </EmtTypography>
                <EmtTypography
                  variant="body2"
                  sx={{
                    color: '#77757F',
                  }}
                >
                  {t('moreThanPetsitter.description')}
                </EmtTypography>
              </Box>
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  zIndex: 1,
                  background: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.25) 112.85%)',
                }}
              />
              <NextImage
                src={VariantImages.IlluMap}
                alt="Illustration map"
                style={{
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  zIndex: 0,
                  objectFit: 'cover',
                }}
              />
            </Box>
          )}
        </Box>
      )}

      {step === 'service' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '5px',
            backgroundColor: '#fff',
            borderRadius: '18px',
            padding: '20px 15px',
          }}
        >
          <EmtTypography variant="body1" fontWeight="700">
            {tp('reservations.newRequests.infoBox.reviews.title')}
          </EmtTypography>
          <NextImage src={VariantImages.ReviewsCreateRequest} alt="Pet sitter reviews" />
        </Box>
      )}

      {step === 'signup' && (
        <>
          <Box
            sx={{
              backgroundColor: '#fff',
              padding: '20px',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <NextImage src={VariantImages.Premium30MillionsAmis} alt="partenaire 30 millions d'amis" />
            <EmtTypography variant="body1">
              <b>{tpremium('baseDogownerPetsitting.sidebar.partner30Millions')}</b>
            </EmtTypography>
          </Box>
          <Box
            sx={{
              backgroundColor: '#fff',
              padding: '20px',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <NextImage src={VariantImages.PremiumHandiChiens} alt="partenaire handi'chiens" />
            <EmtTypography variant="body1">
              <b>{tpremium('baseDogownerPetsitting.sidebar.partnerHandiChiens')}</b>
            </EmtTypography>
          </Box>
        </>
      )}
      {!(createMission && step === 'informations') && (
        <Box>
          <EmtTypography
            variant="caption"
            color="textSecondary"
            sx={{
              display: 'block',
              lineHeight: '1.3',
            }}
          >
            Vous avez déjà un compte sur Super Sitter ?
          </EmtTypography>
          <NextLink href={Routes.Connection.SignIn} passHref legacyBehavior>
            <EmtTypography
              variant="caption"
              color="purple.main"
              sx={{
                cursor: 'pointer',
                display: 'block',
                lineHeight: '1.3',
                textDecoration: 'underline',
                fontWeight: '700',
              }}
            >
              Connectez-vous
            </EmtTypography>
          </NextLink>
        </Box>
      )}
    </Box>
  )
}

export default ReservationInfos
